import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { LogDetail, LogDetailChangeType } from '../../../../../../../types/log.interface';
import { CesIconComponent } from '../../../../../../ui/ces-icon/ces-icon.component';
import { isI18nKey } from './i18n.utils';

@Component({
  selector: 'ces-value-change',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CesIconComponent, TranslocoModule],
  templateUrl: './value-change.component.html',
})
export class ValueChangeComponent {
  private readonly translate = inject(TranslocoService);
  readonly detail = input.required<LogDetail>();
  readonly showActionText = input(false);

  readonly LogDetailChangeType = LogDetailChangeType;

  getFormattedValue(value: string | null | undefined): string {
    if (!value) return '';
    if (isI18nKey(value)) {
      const translated = this.translate.translate(value);
      if (translated !== value) return translated;
    }
    return this.formatValue(value);
  }

  getCreatedText(): string {
    const isFeminine = this.showActionText() && this.detail().entity === 'campaign';
    return this.translate.translate(isFeminine ? 'history.logContent.createdFeminine' : 'history.logContent.created');
  }

  getDeletedText(): string {
    const isFeminine = this.showActionText() && this.detail().entity === 'campaign';
    return this.translate.translate(isFeminine ? 'history.logContent.deletedFeminine' : 'history.logContent.deleted');
  }

  private formatValue(value: string): string {
    return this.isValidDate(value) ? this.formatSwissDate(value) : value;
  }

  private isValidDate(value: string): boolean {
    const datePatterns = [/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}/, /^\d{4}-\d{2}-\d{2}$/];

    return datePatterns.some(pattern => pattern.test(value)) && !isNaN(new Date(value).getTime());
  }

  private formatSwissDate(dateString: string): string {
    try {
      const date = new Date(dateString);

      if (isNaN(date.getTime())) return dateString;

      const formatPart = (num: number, pad = 2): string => num.toString().padStart(pad, '0');

      const time = `${formatPart(date.getHours())}:${formatPart(date.getMinutes())}`;
      const dateStr = `${formatPart(date.getDate())}.${formatPart(date.getMonth() + 1)}.${date.getFullYear()}`;

      return `${time} - ${dateStr}`;
    } catch {
      return dateString;
    }
  }
}
